import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import * as api from "@/lib/reading";
import type { ReadingFolder, ReadingLog } from "@/lib/reading";
import { newId } from "@/lib/reading";
import { translateSentence } from "@/lib/translate";
import { parseTexts, parseText, type ParsedToken } from "@/lib/search";

import {
  EMPTY_TAB_STATE,
  closeTab as closeTabState,
  moveTab as moveTabState,
  loadTabState,
  openLog as openLogState,
  reconcile,
  saveTabState,
  setFocused as setFocusedState,
  type TabState,
} from "./logTabs";
import { insertByTime, mergeLoaded, nextFocusId, toEntry, type LogEntry } from "./useReadingLog";

/** What a photo capture (日本-711) adds to a sentence at creation. */
export interface PhotoCapture {
  /** The staged straightened screen, as a field value (`<img src="capture:…">`). */
  image: string;
  /** EXIF `DateTimeOriginal` of the photo, verbatim — or null when it carried none. */
  photoTakenAt: string | null;
}

/**
 * The reading library: folders, logs, the open tabs, and the log you are reading (日本-207).
 *
 * **Owned by `App`, not by `Read`.** A rail click unmounts whatever surface is showing, so a log
 * held inside `Read` was destroyed by a trip to Cards and back. `App` never unmounts, which is also
 * what lets a capture arrive while you are somewhere else.
 *
 * Three rules govern everything below, and each of them has a way of being quietly undone:
 *
 * 1. **Optimistic local write, request behind.** The reading loop is hotkey-paced and must never
 *    stall on a round trip — the same shape `useDeckCounts`' optimistic ±1 uses. A failed write
 *    surfaces one message; it does not roll the sentence back out from under you, because the text
 *    is on screen and being told it is gone is worse than being told it is unsaved.
 * 2. **Ids are minted here, synchronously.** See `LogEntry.id`.
 * 3. **Writes are keyed by sentence id alone**, never by log. The tray's screenshot can land after
 *    you have switched tabs, and `PATCH /sentences/{id}` needs no log — so `setImage` searches
 *    every loaded log rather than the active one.
 */
export interface Reading {
  // --- the library -------------------------------------------------------------------------
  folders: ReadingFolder[];
  logs: ReadingLog[];
  /** False until the first library read lands. `append` works anyway — see `pendingWrites`. */
  ready: boolean;
  /**
   * The last write or read that failed, for the one line Read shows.
   *
   * **Cleared by `dismissError` and by nothing else** — deliberately, and it is worth saying
   * because the obvious alternative is wrong here. Writes are optimistic and fire-and-forget, so
   * "the next success" is whatever request happens to land next, not a retry of the one that
   * failed: a `touch` on tab switch would wipe the line telling you a sentence never saved.
   */
  error: string | null;
  dismissError: () => void;

  // --- tabs (per device, never synced) ------------------------------------------------------
  openLogIds: string[];
  activeLogId: string | null;
  activeLog: ReadingLog | null;
  setActiveLog: (logId: string) => void;
  /** Open a log — or focus the tab it already has. Never a second tab onto one record. */
  openLog: (logId: string) => void;
  closeTab: (logId: string) => void;
  /**
   * Re-arrange the tab strip. **Local only** — tab order is `openLogIds`, per device.
   *
   * Deliberately not the same operation as the library's reordering, which writes `sortOrder` to
   * the server: that is where a log is *filed*, this is which few you have open on this machine.
   */
  moveTab: (movedId: string, targetId: string, before: boolean) => void;
  /** A fresh unsaved log, opened and active. Real, capturing, persisted — just not filed. */
  newLog: () => Promise<string | null>;

  // --- the active log ----------------------------------------------------------------------
  entries: LogEntry[];
  /** Where images land, and the one row drawn at full size (日本-306). */
  focusedId: string | null;
  setFocused: (sentenceId: string | null) => void;
  /**
   * Parse these sentences if they have not been parsed already (日本-602).
   *
   * Read derives the ids from its own window — see `logWindow.parseTargets`. Opening a log no
   * longer parses it, so this is what puts furigana and underlines on the rows being read.
   */
  ensureParsed: (ids: readonly string[]) => void;

  /**
   * Add a captured sentence.
   *
   * `targetLogId` names the log it belongs in, and is only ever sent by a **capture region** — the
   * tray agent's fixed rectangles, each of which may pin one log (日本-463). Everything else omits
   * it and lands in the active log, which is what every capture did before regions existed.
   *
   * **An id this device does not know falls back to the active log rather than dropping the
   * sentence.** A log deleted on another machine, or a library read that has not landed yet, must
   * not cost a capture — and the text is on screen either way, which is rule 1's trade.
   */
  append: (
    text: string,
    onCreated?: (id: string) => void,
    targetLogId?: string | null,
    /** Read's photo capture only (日本-711): the straightened screen and its EXIF time. */
    photo?: PhotoCapture,
  ) => Promise<string | null>;
  replaceText: (id: string, text: string) => Promise<void>;
  setImage: (id: string, which: "image" | "extraImage", value: string) => void;
  beginImage: (id: string, which: "image" | "extraImage") => void;
  noteMined: (id: string, noteId: number) => void;
  remove: (id: string) => void;
  /**
   * Ask for the English for one sentence (日本-102). **Only ever from a button.**
   *
   * Unlike every other write here this one is *not* optimistic — there is nothing to write until
   * the answer arrives — so the row shows `translating…` and the request is awaited. `retranslate`
   * is this same call: the reader presses it because they disagreed with the answer.
   */
  translate: (id: string) => Promise<void>;
  /** The reader's own correction, which beats the model's sentence. `""` returns the row to bare. */
  setTranslation: (id: string, text: string) => void;
  /** Repaint a translation that is already stored — Ask's agent wrote it (日本-325). */
  applyTranslation: (id: string, text: string) => void;
  /** Move a sentence to another log. It keeps its timestamp, so it lands in reading order. */
  moveSentence: (id: string, toLogId: string) => void;

  // --- log and folder administration -------------------------------------------------------
  renameLog: (logId: string, name: string | null) => void;
  fileLog: (logId: string, folderId: string | null) => void;
  /**
   * Apply a library drag: a new order, and — when the dragged log changed folder — its new home.
   *
   * `folderId` travels with `movedId` in the **same** PATCH rather than as a separate `fileLog`
   * call, because a log's position is only meaningful inside its folder: two writes would leave the
   * row briefly ordered against a group it is not in, and a failure between them would leave it
   * there for good.
   */
  reorderLogs: (
    reorders: readonly { id: string; sortOrder: number }[],
    moved?: { id: string; folderId: string | null },
  ) => void;
  reorderFolders: (reorders: readonly { id: string; sortOrder: number }[]) => void;
  removeLog: (logId: string) => Promise<void>;
  clearLog: (logId: string) => Promise<void>;
  createFolder: (name: string) => Promise<void>;
  renameFolder: (folderId: string, name: string) => void;
  removeFolder: (folderId: string) => Promise<void>;
}

export function useReading(): Reading {
  const [folders, setFolders] = useState<ReadingFolder[]>([]);
  const [logs, setLogs] = useState<ReadingLog[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabs, setTabs] = useState<TabState>(EMPTY_TAB_STATE);
  /** Loaded sentences, per log, so switching back to a tab is instant rather than a fetch. */
  const [byLog, setByLog] = useState<Record<string, LogEntry[]>>({});

  /**
   * Logs whose sentences this device has actually accounted for — fetched from the server, or
   * created here and therefore known to be empty.
   *
   * **Deliberately not `byLog[logId] !== undefined`, which is what it used to be.** That conflated
   * "we have rows for this log" with "we have *all* the rows", and an optimistic `append` writes
   * `[row]` into an unloaded log (`current[target] ?? []`) — so a capture arriving before the fetch
   * marked the log loaded and the fetch never ran. The log then showed exactly one sentence while
   * its own header counted seven, for the life of the tab.
   *
   * That is the `/?q=` deep link, which is the desktop shell's cold start and therefore routine
   * rather than rare: the agent launches the app *with* a capture, so the append always races the
   * load. A ref rather than state because nothing renders from it and it must be readable
   * synchronously inside the load effect.
   */
  const loaded = useRef(new Set<string>());

  /**
   * The live active-log id, for callbacks that must not re-create on every tab change.
   *
   * `append` is handed to the agent bridge in `App`, and a dependency on the active tab would make
   * the effect that registers it re-run mid-capture.
   */
  const activeRef = useRef<string | null>(null);
  activeRef.current = tabs.activeLogId;

  /**
   * The live log list, for the same reason `activeRef` exists.
   *
   * `append` is handed to the agent bridge in `App`, and taking `logs` as a dependency would
   * re-create it — and re-run the effect that registers it — on every rename, every count bump and
   * every capture. It is read to check that a capture region's target log actually exists.
   */
  const logsRef = useRef<readonly ReadingLog[]>([]);
  logsRef.current = logs;
  /**
   * The live sentences, for `ensureParsed` — which reads a sentence's TEXT at the moment it asks
   * about it. Its caller hands over ids alone, so it cannot be given a stale copy of a sentence
   * that has since been edited, and it does not have to join `byLog` to its own dependency list
   * (which changes on every capture and would rebuild the callback constantly).
   */
  const byLogRef = useRef<Record<string, LogEntry[]>>({});
  byLogRef.current = byLog;

  /**
   * Sentences captured before the library had loaded, waiting for a log to put them in.
   *
   * **The `/?q=` deep link is exactly this case** and it is the app's headline path: the tray agent
   * opens a fresh tab with the capture in the URL, `App`'s seed effect fires on mount, and the
   * library read has not come back yet. Without a queue the sentence would be POSTed to `null` and
   * lost — the one failure this whole feature exists to prevent.
   */
  const pendingWrites = useRef<((logId: string) => void)[]>([]);

  /**
   * Captured rows that are not in any loaded log **yet**, by id.
   *
   * The window is short and entirely real: `append` mints an id and hands it out synchronously, and
   * the deep-link path then calls `setImage` on it — which searches the loaded logs and would find
   * nothing. Patching the staged copy instead means the screenshot is carried into the row when it
   * lands rather than silently dropped.
   */
  const staged = useRef(new Map<string, LogEntry>());

  /**
   * The create request for each captured sentence, so every later write chains behind it.
   *
   * **A `PATCH /sentences/{id}` that overtakes its own `POST` is a 404**, and it is reachable
   * without any of the above: paste a screenshot within a few hundred milliseconds of the capture
   * and the two requests race. The image would then be on screen, absent from the database, and
   * gone on the next reload — the worst shape of failure this surface can have.
   *
   * A deferred promise rather than the POST's own, because it has to exist from the moment the id
   * does — which is before there is a log to POST to. Sentences loaded from the server have no
   * entry here at all and write immediately, which is right: they demonstrably exist.
   */
  const creates = useRef(new Map<string, Promise<unknown>>());
  const created = useRef(new Map<string, () => void>());

  /** Sentences with a translate request out, so a held `T` cannot fire one per keypress. */
  const inFlight = useRef(new Set<string>());

  /** Per-entry parse sequence, so two quick edits cannot leave the first edit's tokens rendered
   * against the second edit's text — only the newest parse for an entry may land. */
  const parseSeq = useRef(new Map<string, number>());

  /**
   * What `ensureParsed` has already been answered about, and what it has asked about.
   *
   * Declared here rather than beside `ensureParsed` because `applyParse` and `loadLog` write to
   * the first of them — see the full account there.
   */
  const parseKnown = useRef<Set<string>>(new Set());
  const parseInFlight = useRef<Set<string>>(new Set());

  const fail = useCallback((cause: unknown, what: string) => {
    setError(cause instanceof Error ? cause.message : `${what}できませんでした。`);
  }, []);

  const dismissError = useCallback(() => setError(null), []);

  /**
   * Run a sentence write once that sentence is known to exist on the server.
   *
   * For a row loaded from the server there is nothing to wait on and this is a plain call. For one
   * captured in this session it waits out the create — see `creates`.
   */
  const afterCreate = useCallback(
    (id: string, run: () => Promise<unknown>, what: string) => {
      void (creates.current.get(id) ?? Promise.resolve())
        .then(run)
        .catch((cause) => fail(cause, what));
    },
    [fail],
  );

  // ---------------------------------------------------------------------------- boot

  useEffect(() => {
    let cancelled = false;
    void api
      .fetchLibrary()
      .then(async ({ folders: loadedFolders, logs: loadedLogs }) => {
        if (cancelled) return;
        setFolders(loadedFolders);
        let restored = reconcile(loadTabState(), loadedLogs);
        let known = loadedLogs;
        if (restored.activeLogId === null) {
          // No logs at all — a fresh deployment, or every log deleted. Mint one rather than
          // rendering a screen with no tab and no way to capture into anything. `reconcile` is
          // pure and cannot do this itself.
          const created = await api.createLog(newId()).catch((cause) => {
            fail(cause, "最初のログを作成");
            return null;
          });
          if (cancelled) return;
          if (created) {
            known = [created];
            restored = { openLogIds: [created.id], activeLogId: created.id, focusedByLog: {} };
          }
        }
        setLogs(known);
        // Eagerly, and this is load-bearing rather than tidy. The ref is normally assigned during
        // RENDER, and the queue below drains in this same callback — before React re-renders — so a
        // queued capture would check its target log against an empty list and fall back to the
        // active one. That is the `/?q=…&log=…` deep link, which is the tray agent's cold start and
        // therefore a capture region's FIRST capture of every session. Same reason `loaded` is a
        // ref: it has to be readable synchronously inside this effect.
        logsRef.current = known;
        setTabs(restored);
        setReady(true);
        // Anything captured while this was in flight now has somewhere to go. The queue is emptied
        // only when there IS somewhere — clearing it unconditionally dropped every queued capture
        // on the one path that reaches here with no log (the mint above having failed), which is
        // exactly the loss the queue exists to prevent.
        const target = restored.activeLogId;
        if (target) {
          const queued = pendingWrites.current;
          pendingWrites.current = [];
          for (const place of queued) place(target);
        }
      })
      .catch(async (cause) => {
        if (cancelled) return;
        // Read stays usable: the surfaces below deliberately swallow API failures so reading works
        // when the hub is down, and this is the same trade. What you lose is persistence, which is
        // why it says so rather than failing silently.
        fail(cause, "読書ログを取得");
        // **The queue still has to drain**, and this is not belt-and-braces. `append` with no
        // active log only stages the row, so a `/?q=` capture that arrived while the library read
        // was failing was left in a ref with nothing on screen at all — the deep link showing
        // "Nothing captured yet" over the sentence it was opened with.
        //
        // A create is tried first, because the library read can fail on its own; a local-only log
        // is the fallback. Its writes then 404 and say so, which is rule 1's trade — the text is on
        // screen and unsaved beats gone.
        const id = newId();
        const created = await api.createLog(id).catch(() => null);
        if (cancelled) return;
        const entry: ReadingLog = created ?? {
          id,
          name: null,
          // Only reached when the create ALSO failed, so there is no server number to carry. `0`
          // renders as `New log 0`, which is honest about being a log the server has never seen.
          seq: 0,
          folderId: null,
          createdAt: Date.now() / 1000,
          lastReadAt: Date.now() / 1000,
          sortOrder: 0,
          sentenceCount: 0,
          minedCount: 0,
        };
        setLogs([entry]);
        // As above: this branch drains the queue too, and a queued capture's target is checked
        // against this ref rather than against state that has not re-rendered yet.
        logsRef.current = [entry];
        // Eagerly empty, so the load effect does not go and fetch a log we already know about.
        loaded.current.add(entry.id);
        setByLog((current) => ({ ...current, [entry.id]: [] }));
        setTabs({ openLogIds: [entry.id], activeLogId: entry.id, focusedByLog: {} });
        setReady(true);
        const queued = pendingWrites.current;
        pendingWrites.current = [];
        for (const place of queued) place(entry.id);
      });
    return () => {
      cancelled = true;
    };
  }, [fail]);

  useEffect(() => {
    if (ready) saveTabState(tabs);
  }, [tabs, ready]);

  // ------------------------------------------------------------------- loading a log

  /**
   * A merged row that came out with no tokens must be askable again. `mergeLoaded` keeps a local
   * row's tokens only when the text matches; where it does not, the row takes the fetched text
   * with empty tokens — and if `applyParse` had already recorded the id in `parseKnown`, the
   * window would never parse it, leaving it plain text until reload (日本-714's shape again).
   * Idempotent, so a StrictMode double-run of the updater is harmless.
   */
  const forgetUnparsed = (list: LogEntry[]): LogEntry[] => {
    for (const entry of list) if (entry.tokens.length === 0) parseKnown.current.delete(entry.id);
    return list;
  };

  const loadLog = useCallback(
    async (logId: string) => {
      // Claimed BEFORE the await, so a re-render while the fetch is in flight cannot start a
      // second one. Released again on failure, below, so a later attempt can still retry.
      loaded.current.add(logId);
      try {
        const { log: fetchedLog, sentences } = await api.fetchLog(logId);
        // **The sentences arrive UNPARSED, and `ensureParsed` fills them in as they are looked
        // at** (日本-602). This used to be one `parse-batch` over the whole log before anything
        // painted, which was right when the whole log rendered — and became the entire cost of
        // opening once the rendering was windowed: 2.6 s and a 9.6 MB response for 3,000
        // sentences, for tokens that only the rows on screen can use.
        //
        // Nothing is lost in the meantime. `Sentence` falls back to the raw text when a row has no
        // tokens, so an unparsed row is readable and editable — it simply has no furigana and no
        // underlines until its parse lands, which for the rows being read is before they arrive.
        setByLog((current) => ({
          ...current,
          // MERGE, never replace — see `mergeLoaded`. A capture that raced this load is already in
          // `current[logId]` and may not have reached the server before the response was built.
          [logId]: forgetUnparsed(
            mergeLoaded(
              sentences.map((sentence) => toEntry(sentence, [])),
              current[logId] ?? [],
            ),
          ),
        }));
        setLogs((current) =>
          current.some((entry) => entry.id === fetchedLog.id)
            ? current.map((entry) => (entry.id === fetchedLog.id ? fetchedLog : entry))
            : [...current, fetchedLog],
        );
      } catch (cause) {
        loaded.current.delete(logId);
        fail(cause, "ログを表示");
      }
    },
    [fail],
  );

  useEffect(() => {
    const logId = tabs.activeLogId;
    if (!ready || !logId || loaded.current.has(logId)) return;
    void loadLog(logId);
    // `byLog` is deliberately not a dependency: it changes on every capture, and re-running this
    // on each one would re-fetch the log you are typing into.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, tabs.activeLogId, loadLog]);

  /** Mark the log as read, so an empty restore lands on the book you were actually reading. */
  useEffect(() => {
    const logId = tabs.activeLogId;
    if (!ready || !logId) return;
    void api.updateLog(logId, { touch: true }).catch(() => undefined);
  }, [ready, tabs.activeLogId]);

  // ----------------------------------------------------------------------- sentences

  const entries = useMemo(
    () => (tabs.activeLogId ? (byLog[tabs.activeLogId] ?? []) : []),
    [byLog, tabs.activeLogId],
  );
  const focusedId = tabs.activeLogId ? (tabs.focusedByLog[tabs.activeLogId] ?? null) : null;

  /** Apply a change to one sentence wherever it is — loaded, or still staged. See rule 3. */
  const patchEntry = useCallback(
    (id: string, change: (entry: LogEntry) => LogEntry) => {
      const pending = staged.current.get(id);
      if (pending) staged.current.set(id, change(pending));
      setByLog((current) => {
        const next: Record<string, LogEntry[]> = {};
        let touched = false;
        for (const [logId, list] of Object.entries(current)) {
          if (!list.some((entry) => entry.id === id)) {
            next[logId] = list;
            continue;
          }
          touched = true;
          next[logId] = list.map((entry) => (entry.id === id ? change(entry) : entry));
        }
        return touched ? next : current;
      });
    },
    [],
  );

  const applyParse = useCallback(async (id: string, text: string) => {
    const seq = (parseSeq.current.get(id) ?? 0) + 1;
    parseSeq.current.set(id, seq);
    let tokens: ParsedToken[] = [];
    try {
      ({ tokens } = await parseText(text));
      // **Recorded as known, on `ensureParsed`'s own terms: a response has been received.** A
      // capture lands at the end of the log, which is inside the rendered window, so the parse
      // effect asks about it in the same beat `append` parses it — without this every capture
      // costs a second, redundant `parse-batch` for a sentence already being parsed here.
      parseKnown.current.add(id);
    } catch {
      // Backend unreachable. Empty tokens make `Sentence` fall back to the raw text, which keeps
      // the capture visible and editable — editing (or re-committing) retries the parse. NOT
      // recorded, so the windowed parse is still free to ask again.
      parseKnown.current.delete(id);
    }
    if (parseSeq.current.get(id) !== seq) return;
    // **A row still staged takes the tokens too** (日本-714). The deep link's capture arrives before
    // the library, so it waits in `pendingWrites` while this parse runs — and the parse usually
    // answers first. Written to `byLog` alone the tokens landed nowhere, while `parseKnown` above
    // told the window never to ask again. `place` carries the staged copy, tokens and all.
    const pending = staged.current.get(id);
    if (pending) staged.current.set(id, { ...pending, tokens });
    setByLog((current) => {
      const next: Record<string, LogEntry[]> = {};
      for (const [logId, list] of Object.entries(current)) {
        next[logId] = list.map((entry) => (entry.id === id ? { ...entry, tokens } : entry));
      }
      return next;
    });
  }, []);

  /**
   * Parse the sentences the reader is at, and remember which ones have been answered.
   *
   * The other half of 日本-602: `loadLog` no longer parses the log, so something has to parse what
   * is on screen. `Read.tsx` derives the ids from its own window (`logWindow.parseTargets`) and
   * calls this; the hook owns `byLog`, so the tokens land here.
   *
   * **`parsedRef` records an id once a response has been received, whether or not it produced
   * tokens.** A sentence that legitimately parses to nothing, and one asked about while the
   * backend was unreachable, both leave `tokens` empty and are indistinguishable from a row nobody
   * asked about — so without this every scroll re-requests the same rows for ever. `inFlightRef`
   * is the same guard for the window of time before the response lands, since a scroll fires far
   * more often than a round trip completes.
   *
   * A failed batch is **forgotten rather than recorded**, so moving away and back retries it; that
   * is the one case where asking again is the right thing, and it is what makes a blip during a
   * capture heal itself rather than leaving a log permanently without underlines. `applyParse`
   * records into the same set for the same reason, from the other side — a sentence it has just
   * parsed must not be asked about again by the window it landed in.
   */
  const ensureParsed = useCallback(async (ids: readonly string[]) => {
    const wanted = ids.filter(
      (id) => !parseKnown.current.has(id) && !parseInFlight.current.has(id),
    );
    if (wanted.length === 0) return;
    // Claimed BEFORE the await, on `loadLog`'s reasoning: a re-render mid-flight must not start a
    // second request for the same rows.
    wanted.forEach((id) => parseInFlight.current.add(id));

    // The texts are read from the live state rather than passed in, so the caller hands over ids
    // alone and cannot send a stale copy of a sentence that has since been edited.
    //
    // A SET rather than `wanted.includes`: this walks every entry of every loaded log, so the
    // linear scan made it the product of the two — ~110 ids against a 2,000-sentence log is
    // 220,000 comparisons per scroll, on the path this whole ticket exists to make cheap.
    const asking = new Set(wanted);
    const texts = new Map<string, string>();
    for (const list of Object.values(byLogRef.current)) {
      for (const entry of list) if (asking.has(entry.id)) texts.set(entry.id, entry.text);
    }
    const asked = wanted.filter((id) => texts.has(id));
    if (asked.length === 0) {
      wanted.forEach((id) => parseInFlight.current.delete(id));
      return;
    }

    try {
      const { results } = await parseTexts(asked.map((id) => texts.get(id) ?? ""));
      const tokensById = new Map<string, ParsedToken[]>();
      // `results` is positional and the same length as what was sent — two sentences in a log are
      // very often identical, which is why the route is not keyed by text.
      asked.forEach((id, index) => tokensById.set(id, results[index]?.tokens ?? []));
      asked.forEach((id) => parseKnown.current.add(id));
      setByLog((current) => {
        const next: Record<string, LogEntry[]> = {};
        let touched = false;
        for (const [logId, list] of Object.entries(current)) {
          next[logId] = list.map((entry) => {
            const tokens = tokensById.get(entry.id);
            // **Only a row that is still unparsed is written.** An edit landing while this was in
            // flight has already put its own tokens on the entry through `applyParse`, and this
            // response describes the text as it was — so overwriting would put the old sentence's
            // tokens under the new one's text.
            if (tokens === undefined || entry.tokens.length > 0) return entry;
            touched = true;
            return { ...entry, tokens };
          });
        }
        return touched ? next : current;
      });
    } catch {
      // Deliberately NOT recorded as known — see above. `Sentence` falls back to the raw text, so
      // the log stays readable, and coming back to these rows asks again.
    } finally {
      wanted.forEach((id) => parseInFlight.current.delete(id));
    }
  }, []);

  const setFocused = useCallback((sentenceId: string | null) => {
    setTabs((current) =>
      current.activeLogId
        ? setFocusedState(current, current.activeLogId, sentenceId)
        : current,
    );
  }, []);

  /**
   * Wrap a queued placement so its target is re-checked when the queue drains.
   *
   * A capture that arrives before the library has loaded — the `/?q=` deep link, which is the tray
   * agent's cold start and therefore the *normal* path — cannot have its target validated at the
   * time, because there is no log list yet. Validating it at boot would mean a region's very first
   * capture of a session is the one that ignores its target.
   */
  const retarget = useCallback(
    (targetLogId: string, place: (logId: string) => void) => (fallbackLogId: string) => {
      const known = logsRef.current.some((entry) => entry.id === targetLogId);
      place(known ? targetLogId : fallbackLogId);
    },
    [],
  );

  const append = useCallback(
    async (
      text: string,
      onCreated?: (id: string) => void,
      targetLogId?: string | null,
      // The phone's photo path (日本-711): the straightened screen goes up WITH the create, so no
      // PATCH follows it, and the camera flag is set here or never — the server only ever clears it.
      photo?: PhotoCapture,
    ): Promise<string | null> => {
      const trimmed = text.trim();
      if (!trimmed) return null;
      const id = newId();
      const at = new Date();
      // Before the state update and before any await: the caller may be racing an upload that has
      // already started, and the frame announcing it can arrive at any moment.
      onCreated?.(id);

      const entry: LogEntry = {
        id,
        at,
        text: trimmed,
        tokens: [],
        image: photo?.image ?? "",
        extraImage: "",
        minedNoteIds: [],
        imageBusy: null,
        translation: "",
        translating: false,
      };

      staged.current.set(id, entry);
      creates.current.set(
        id,
        new Promise<void>((resolve) => created.current.set(id, resolve)),
      );

      const place = (target: string) => {
        // The STAGED copy, not the entry built above: a screenshot or an edit may have landed on it
        // while it was waiting, and those values go up with the create rather than in a PATCH that
        // would have to be ordered behind it.
        const row = staged.current.get(id) ?? entry;
        staged.current.delete(id);
        setByLog((current) => ({ ...current, [target]: [...(current[target] ?? []), row] }));
        // **The target's tab is opened and made active, not just focused within.** A capture pinned
        // to a log you are not looking at otherwise arrives invisibly: the sentence is stored, and
        // the screen still says `まだ何も取り込んでいません` — over the very capture it was opened to
        // receive. Seen in the browser on the `/?q=…&log=…` deep link, which is the tray agent's
        // cold start and so the commonest way a capture region's sentence arrives.
        //
        // Doing it HERE rather than at the two call sites in `App` is what makes it uniform: the
        // bridge path, the deep link and the drained boot queue all come through `place`.
        // `openLogState` is idempotent and never makes a second tab onto one record, so for an
        // ordinary capture into the active log this is a no-op.
        setTabs((current) => setFocusedState(openLogState(current, target), target, id));
        bumpCount(setLogs, target, 1);
        void api
          .addSentence(target, {
            id,
            text: row.text,
            capturedAt: at.getTime() / 1000,
            image: row.image,
            extraImage: row.extraImage,
            ...(photo ? { imageSource: "camera" as const, photoTakenAt: photo.photoTakenAt } : {}),
          })
          .catch((cause) => fail(cause, "文を保存"))
          .finally(() => {
            // Releases every write queued behind this sentence — on failure too. A create that
            // failed leaves the PATCHes to 404 and say so, which beats a queue that never drains
            // and a slot stuck on `storing…` for the life of the tab.
            created.current.get(id)?.();
            created.current.delete(id);
            // Dropped once it has settled, for `parseSeq`'s reason: an evening of capturing is
            // thousands of entries in a map nothing ever reads again. A later `afterCreate` then
            // finds nothing and runs immediately, which is right — the create demonstrably
            // finished. Writes that already chained off it keep their own reference.
            creates.current.delete(id);
          });
      };

      // A capture region's target wins over the active tab — that is the whole point of pinning
      // one. Resolved against `logsRef` rather than against `byLog`, because a log this device has
      // never opened is still a perfectly good target and has no entry there.
      //
      // **An unknown id falls through to the active log rather than being honoured blindly.**
      // POSTing to a log the server does not have loses the sentence, and a log deleted on another
      // device is the ordinary way that happens.
      const targeted =
        targetLogId && logsRef.current.some((entry) => entry.id === targetLogId)
          ? targetLogId
          : null;

      const logId = targeted ?? activeRef.current;
      if (logId) place(logId);
      // No active log yet: the library read is still in flight. Queued rather than dropped — this
      // is the `/?q=` deep link, which is the first capture of a session.
      //
      // A targeted capture queues the same way and is re-resolved when it drains: at boot the
      // library is not loaded, so the target cannot be checked yet and dropping it here would make
      // a region's very first capture the one that ignores its target.
      else pendingWrites.current.push(targetLogId ? retarget(targetLogId, place) : place);

      // Returned after the parse, so an `await` here keeps one meaning: the entry exists and is
      // parsed. A screenshot is seconds behind either way.
      await applyParse(id, trimmed);
      return id;
    },
    [applyParse, fail, retarget],
  );

  /** Re-parse a sentence in place after an edit. OCR is imperfect and this is the fix. */
  const replaceText = useCallback(
    async (id: string, text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      patchEntry(id, (entry) => ({ ...entry, text: trimmed }));
      // Skipped entirely while the row is staged: the create has not gone yet and will carry the
      // new text itself, so a PATCH here would only be a 404 waiting to happen.
      if (!staged.current.has(id)) {
        afterCreate(id, () => api.updateSentence(id, { text: trimmed }), "編集を保存");
      }
      await applyParse(id, trimmed);
    },
    [applyParse, patchEntry, afterCreate],
  );

  const setImage = useCallback(
    (id: string, which: "image" | "extraImage", value: string) => {
      patchEntry(id, (entry) => ({
        ...entry,
        [which]: value,
        // Clearing `imageBusy` only when it was THIS slot: pasting into Extra image while the
        // agent's screenshot is still landing in Image must not drop the other one's pending mark.
        imageBusy: entry.imageBusy === which ? null : entry.imageBusy,
      }));
      // Same as the edit above: a staged row carries its images up with the create.
      if (staged.current.has(id)) return;
      afterCreate(
        id,
        () =>
          api.updateSentence(id, which === "extraImage" ? { extraImage: value } : { image: value }),
        "画像を保存",
      );
    },
    [patchEntry, afterCreate],
  );

  const beginImage = useCallback(
    (id: string, which: "image" | "extraImage") => {
      // Local only — it describes an upload happening in another process right now, and a reload
      // that restored it would show `storing…` for ever.
      patchEntry(id, (entry) => ({ ...entry, imageBusy: which }));
    },
    [patchEntry],
  );

  const noteMined = useCallback(
    (id: string, noteId: number) => {
      const logId = findLogOf(byLog, id) ?? activeRef.current;
      // Decided out here, not inside the updater — StrictMode double-invokes those, so a flag set
      // in one counts twice. The server's `note_mined` is a SET, so a repeat records nothing;
      // bumping `minedCount` regardless made the library's delete confirmation ("61 mined cards")
      // count an add that never happened, and nothing corrects it until the next library read.
      const held =
        staged.current.get(id) ?? (logId ? byLog[logId] : undefined)?.find((e) => e.id === id);
      const isNew = held === undefined || !held.minedNoteIds.includes(noteId);

      patchEntry(id, (entry) =>
        entry.minedNoteIds.includes(noteId)
          ? entry
          : { ...entry, minedNoteIds: [...entry.minedNoteIds, noteId] },
      );
      afterCreate(id, () => api.noteMined(id, noteId), "作成したカードを記録");
      if (!isNew) return;
      setLogs((current) =>
        current.map((entry) =>
          entry.id === logId ? { ...entry, minedCount: entry.minedCount + 1 } : entry,
        ),
      );
    },
    [patchEntry, byLog, afterCreate],
  );

  /**
   * Ask for the English for one sentence, and write it onto the row (日本-102).
   *
   * **The one write here that is not optimistic**, and the exception proves rule 1 rather than
   * bending it: that rule exists because the reading loop is hotkey-paced and must not stall on a
   * round trip, and there is nothing to render locally until the answer exists. So the row shows
   * `translating…` — which is `imageBusy`'s shape, for `imageBusy`'s reason: it describes a request
   * in flight and must never be persisted.
   *
   * **Chained through `afterCreate` like every other sentence write.** The server has to be able
   * to find the row, and translating a sentence captured half a second ago otherwise overtakes its
   * own POST and 404s — the same race the images hit, reachable by pressing `T` as soon as the
   * capture lands.
   */
  const translate = useCallback(
    async (id: string) => {
      // **A ref, not the `translating` flag, is what stops a second request.** The flag is state
      // read through this callback's closure, so it is one render behind — and `T` autorepeats at
      // ~30/s while held, which is far inside that window. The flag is what the row *draws*; this
      // is what the request path checks.
      if (inFlight.current.has(id)) return;
      const held = staged.current.get(id) ?? entryById(byLog, id);
      if (!held) return;
      inFlight.current.add(id);
      patchEntry(id, (entry) => ({ ...entry, translating: true }));
      try {
        await (creates.current.get(id) ?? Promise.resolve());
        const { translation } = await translateSentence(id);
        // `translating` is cleared in the same patch as the text, never in a `finally` that runs
        // after it: two updates would render one frame with the English present and the bar still
        // showing, which reads as a second request having started.
        patchEntry(id, (entry) => ({ ...entry, translation, translating: false }));
      } catch (cause) {
        patchEntry(id, (entry) => ({ ...entry, translating: false }));
        fail(cause, "文を翻訳");
      } finally {
        // Released on failure too, or a row that failed once could never be retried — which is the
        // one thing a reader looking at an error line is certain to try.
        inFlight.current.delete(id);
      }
    },
    [byLog, patchEntry, fail],
  );

  /**
   * The reader's correction, or its removal — `""` returns the row to bare.
   *
   * Optimistic, unlike `translate`: the text is already in hand, so rule 1 applies exactly as it
   * does to editing the Japanese.
   */
  const setTranslation = useCallback(
    (id: string, text: string) => {
      patchEntry(id, (entry) => ({ ...entry, translation: text }));
      if (staged.current.has(id)) return;
      afterCreate(
        id,
        () => api.updateSentence(id, { translation: text }),
        "訳を保存",
      );
    },
    [patchEntry, afterCreate],
  );

  /**
   * A translation that is **already saved** — repaint the row and write nothing (日本-325).
   *
   * Ask's agent writes the field itself, through `set_translation`, and announces it on the stream.
   * By the time that frame arrives the value is in Postgres, so `setTranslation` here would be a
   * second PATCH of a value that is already stored — wasted, and a race with any correction the
   * reader has begun in the meantime. What is left to do is the half the server cannot: repaint.
   */
  const applyTranslation = useCallback(
    (id: string, text: string) => {
      patchEntry(id, (entry) => ({ ...entry, translation: text }));
    },
    [patchEntry],
  );

  /**
   * Drop a capture.
   *
   * OCR sometimes reads the UI chrome instead of the dialogue, and a log you cannot prune is a log
   * you stop scrolling back through. Cards already mined from it are untouched — they live in the
   * collection, not here.
   */
  const remove = useCallback(
    (id: string) => {
      const logId = findLogOf(byLog, id);
      if (!logId) return;
      // The parse sequence outlives the entry otherwise. It cannot resurrect anything — ids are
      // UUIDs and never reissued — but leaving it is a map that only grows.
      parseSeq.current.delete(id);
      const before = byLog[logId] ?? [];
      const after = before.filter((entry) => entry.id !== id);
      // Computed out here rather than inside a `setByLog` updater: StrictMode double-invokes
      // updaters, and a `setTabs` nested in one would run twice.
      const nextFocus = nextFocusId(before, after, tabs.focusedByLog[logId] ?? null, id);
      const mined = before.find((entry) => entry.id === id)?.minedNoteIds.length ?? 0;
      setByLog((current) => ({ ...current, [logId]: after }));
      setTabs((current) => setFocusedState(current, logId, nextFocus));
      bumpCount(setLogs, logId, -1, -mined);
      afterCreate(id, () => api.deleteSentence(id), "文を削除");
    },
    [byLog, tabs.focusedByLog, afterCreate],
  );

  /**
   * Move a sentence to another log.
   *
   * It carries its images, its timestamp and its mined-card links, and lands in **timestamp
   * order** at the far end rather than at the bottom — a log is a record of reading, so a moved
   * sentence must not claim it was read last. `insertByTime` applies that rule locally so the row
   * appears in the right place immediately instead of jumping when the response lands.
   */
  const moveSentence = useCallback(
    (id: string, toLogId: string) => {
      const fromLogId = findLogOf(byLog, id);
      if (!fromLogId || fromLogId === toLogId) return;
      const entry = (byLog[fromLogId] ?? []).find((candidate) => candidate.id === id);
      if (!entry) return;

      setByLog((current) => {
        const next = {
          ...current,
          [fromLogId]: (current[fromLogId] ?? []).filter((candidate) => candidate.id !== id),
        };
        // Only if the destination is loaded. Inserting into a log we have never read would build a
        // one-row cache that the tab then trusts instead of fetching, and the other 400 sentences
        // would simply not be there.
        if (current[toLogId] !== undefined) next[toLogId] = insertByTime(current[toLogId], entry);
        return next;
      });
      setTabs((current) => {
        const focused = current.focusedByLog[fromLogId];
        if (focused !== id) return current;
        const before = byLog[fromLogId] ?? [];
        const after = before.filter((candidate) => candidate.id !== id);
        return setFocusedState(current, fromLogId, nextFocusId(before, after, id, id));
      });
      // The mined-card links travel with the sentence, so the counts have to as well — the row
      // says so and the server's `update_sentence` does exactly that.
      const mined = entry.minedNoteIds.length;
      bumpCount(setLogs, fromLogId, -1, -mined);
      bumpCount(setLogs, toLogId, 1, mined);
      afterCreate(id, () => api.updateSentence(id, { logId: toLogId }), "文を移動");
    },
    [byLog, afterCreate],
  );

  // ---------------------------------------------------------------------------- tabs

  const setActiveLog = useCallback((logId: string) => {
    setTabs((current) => ({ ...current, activeLogId: logId }));
  }, []);

  const openLog = useCallback((logId: string) => {
    setTabs((current) => openLogState(current, logId));
  }, []);

  const closeTab = useCallback((logId: string) => {
    setTabs((current) => closeTabState(current, logId));
  }, []);

  const moveTab = useCallback((movedId: string, targetId: string, before: boolean) => {
    setTabs((current) => moveTabState(current, movedId, targetId, before));
  }, []);

  const newLog = useCallback(async () => {
    const id = newId();
    try {
      const created = await api.createLog(id);
      setLogs((current) => [...current, created]);
      // Loaded eagerly and empty, so the tab does not fetch a log we know has no sentences.
      loaded.current.add(id);
      setByLog((current) => ({ ...current, [id]: [] }));
      setTabs((current) => openLogState(current, id));
      return id;
    } catch (cause) {
      fail(cause, "ログを作成");
      return null;
    }
  }, [fail]);

  // ------------------------------------------------------------- logs and folders

  const renameLog = useCallback(
    (logId: string, name: string | null) => {
      const trimmed = name?.trim() || null;
      setLogs((current) =>
        current.map((entry) => (entry.id === logId ? { ...entry, name: trimmed } : entry)),
      );
      // ONLY `name`. Sending a whole log object here would carry `folderId` with it, and the
      // server cannot tell "the caller means the root" from "the caller did not think about it" —
      // so every rename would unfile the log.
      void api.updateLog(logId, { name: trimmed }).catch((cause) => fail(cause, "ログの名前を変更"));
    },
    [fail],
  );

  const fileLog = useCallback(
    (logId: string, folderId: string | null) => {
      setLogs((current) =>
        current.map((entry) => (entry.id === logId ? { ...entry, folderId } : entry)),
      );
      void api.updateLog(logId, { folderId }).catch((cause) => fail(cause, "ログを移動"));
      void api.fetchLibrary().then(({ folders: fresh }) => setFolders(fresh)).catch(() => undefined);
    },
    [fail],
  );

  const reorderLogs = useCallback(
    (
      reorders: readonly { id: string; sortOrder: number }[],
      moved?: { id: string; folderId: string | null },
    ) => {
      if (reorders.length === 0 && !moved) return;
      const order = new Map(reorders.map((change) => [change.id, change.sortOrder]));
      setLogs((current) =>
        current.map((entry) => {
          const sortOrder = order.get(entry.id) ?? entry.sortOrder;
          const folderId = moved?.id === entry.id ? moved.folderId : entry.folderId;
          return { ...entry, sortOrder, folderId };
        }),
      );
      // Only the rows that actually moved — `reorder` already narrowed to those. The dragged log's
      // folder rides on its own PATCH; every other row gets a bare `sortOrder`.
      const targets = new Set(reorders.map((change) => change.id));
      if (moved) targets.add(moved.id);
      for (const id of targets) {
        const body: { sortOrder?: number; folderId?: string | null } = {};
        const sortOrder = order.get(id);
        if (sortOrder !== undefined) body.sortOrder = sortOrder;
        if (moved?.id === id) body.folderId = moved.folderId;
        void api.updateLog(id, body).catch((cause) => fail(cause, "並び順を保存"));
      }
      // The folder roll-ups (`3 logs · 412 sentences`) are the server's, computed from `folder_id`,
      // so a re-file has to re-read them rather than guess. Only when something was re-filed:
      // a pure reorder cannot change any folder's totals.
      if (moved) {
        void api
          .fetchLibrary()
          .then(({ folders: fresh }) => setFolders(fresh))
          .catch(() => undefined);
      }
    },
    [fail],
  );

  const reorderFolders = useCallback(
    (reorders: readonly { id: string; sortOrder: number }[]) => {
      if (reorders.length === 0) return;
      const order = new Map(reorders.map((change) => [change.id, change.sortOrder]));
      setFolders((current) =>
        current.map((entry) => ({ ...entry, sortOrder: order.get(entry.id) ?? entry.sortOrder })),
      );
      for (const change of reorders) {
        void api
          .updateFolder(change.id, { sortOrder: change.sortOrder })
          .catch((cause) => fail(cause, "並び順を保存"));
      }
    },
    [fail],
  );

  const removeLog = useCallback(
    async (logId: string) => {
      try {
        await api.deleteLog(logId);
      } catch (cause) {
        fail(cause, "ログを削除");
        return;
      }
      setLogs((current) => current.filter((entry) => entry.id !== logId));
      setByLog((current) => {
        const next = { ...current };
        delete next[logId];
        return next;
      });
      setTabs((current) => closeTabState(current, logId));
      void api.fetchLibrary().then(({ folders: fresh }) => setFolders(fresh)).catch(() => undefined);
    },
    [fail],
  );

  const clearLog = useCallback(
    async (logId: string) => {
      try {
        await api.clearLog(logId);
      } catch (cause) {
        fail(cause, "ログを消去");
        return;
      }
      setByLog((current) => ({ ...current, [logId]: [] }));
      setTabs((current) => setFocusedState(current, logId, null));
      setLogs((current) =>
        current.map((entry) =>
          entry.id === logId ? { ...entry, sentenceCount: 0, minedCount: 0 } : entry,
        ),
      );
    },
    [fail],
  );

  const createFolder = useCallback(
    async (name: string) => {
      try {
        const created = await api.createFolder(newId(), name);
        setFolders((current) => [...current, created]);
      } catch (cause) {
        fail(cause, "フォルダを作成");
      }
    },
    [fail],
  );

  const renameFolder = useCallback(
    (folderId: string, name: string) => {
      setFolders((current) =>
        current.map((entry) => (entry.id === folderId ? { ...entry, name } : entry)),
      );
      void api.updateFolder(folderId, { name }).catch((cause) => fail(cause, "フォルダの名前を変更"));
    },
    [fail],
  );

  const removeFolder = useCallback(
    async (folderId: string) => {
      try {
        await api.deleteFolder(folderId);
      } catch (cause) {
        fail(cause, "フォルダを削除");
        return;
      }
      setFolders((current) => current.filter((entry) => entry.id !== folderId));
      // Its logs SURVIVE, at the root — the server's FK is `ON DELETE SET NULL`. Mirrored here so
      // the library does not have to be re-read to stop showing them under a folder that is gone.
      setLogs((current) =>
        current.map((entry) => (entry.folderId === folderId ? { ...entry, folderId: null } : entry)),
      );
    },
    [fail],
  );

  const activeLog = useMemo(
    () => logs.find((entry) => entry.id === tabs.activeLogId) ?? null,
    [logs, tabs.activeLogId],
  );

  return {
    folders,
    logs,
    ready,
    error,
    dismissError,
    openLogIds: tabs.openLogIds,
    activeLogId: tabs.activeLogId,
    activeLog,
    setActiveLog,
    openLog,
    closeTab,
    moveTab,
    newLog,
    entries,
    focusedId,
    setFocused,
    ensureParsed,
    append,
    replaceText,
    setImage,
    beginImage,
    noteMined,
    remove,
    translate,
    setTranslation,
    applyTranslation,
    moveSentence,
    renameLog,
    fileLog,
    reorderLogs,
    reorderFolders,
    removeLog,
    clearLog,
    createFolder,
    renameFolder,
    removeFolder,
  };
}

/** One loaded sentence, wherever it is. Rule 3's lookup, for readers rather than writers. */
function entryById(byLog: Record<string, LogEntry[]>, sentenceId: string): LogEntry | undefined {
  for (const entries of Object.values(byLog)) {
    const found = entries.find((entry) => entry.id === sentenceId);
    if (found) return found;
  }
  return undefined;
}

/** Which loaded log holds this sentence. Null when it is in a log nothing has opened. */
function findLogOf(byLog: Record<string, LogEntry[]>, sentenceId: string): string | null {
  for (const [logId, entries] of Object.entries(byLog)) {
    if (entries.some((entry) => entry.id === sentenceId)) return logId;
  }
  return null;
}

/**
 * Keep a log's `sentenceCount` and `minedCount` honest without re-reading the library.
 *
 * The counts are shown in the tab strip and in every library row, so leaving them to the next
 * `/library` read would have a tab say `41` through a whole evening of capturing into it.
 *
 * **`mined` travels with the sentence and is not optional.** The library's delete confirmation
 * states what it is about to take (`283 sentences, 61 mined cards`), so a removed or moved row
 * whose mined cards stayed on the count leaves that number naming cards the log no longer has —
 * and a *moved* row would have the destination under-report by exactly as much.
 */
function bumpCount(
  setLogs: React.Dispatch<React.SetStateAction<ReadingLog[]>>,
  logId: string,
  delta: number,
  mined = 0,
) {
  setLogs((current) =>
    current.map((entry) =>
      entry.id === logId
        ? {
            ...entry,
            sentenceCount: Math.max(0, entry.sentenceCount + delta),
            minedCount: Math.max(0, entry.minedCount + mined),
          }
        : entry,
    ),
  );
}
